import React from 'react';
import { Employee, TimesheetDayStatus } from '../../types';
import { daysInMonth, weekdayLabel, cycleStatus, countWorkedDays, statusDisplay, tableInputCls } from './timesheetUtils';

export interface TimesheetRow {
  documentId?: string;
  id?: string | number;
  days: Record<string, TimesheetDayStatus>;
  description: string;
}

interface TimesheetGridProps {
  employees: Employee[];
  month: string; // yyyy-MM
  rows: Record<string, TimesheetRow>; // keyed by employee documentId
  onChange: (employeeKey: string, row: TimesheetRow) => void;
}

const TimesheetGrid: React.FC<TimesheetGridProps> = ({ employees, month, rows, onChange }) => {
  const totalDays = daysInMonth(month);
  const dayNumbers = Array.from({ length: totalDays }, (_, i) => i + 1);

  const toggleDay = (employeeKey: string, day: number) => {
    const row = rows[employeeKey] || { days: {}, description: '' };
    const next = cycleStatus(row.days[String(day)]);
    onChange(employeeKey, { ...row, days: { ...row.days, [String(day)]: next } });
  };

  const setDescription = (employeeKey: string, description: string) => {
    const row = rows[employeeKey] || { days: {}, description: '' };
    onChange(employeeKey, { ...row, description });
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="sticky left-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap z-10">Employee</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Rank</th>
            {dayNumbers.map((day) => (
              <th key={day} className="px-1 py-2 text-center text-xs font-medium text-gray-500">
                <div>{day}</div>
                <div className="text-[10px] text-gray-400">{weekdayLabel(month, day)}</div>
              </th>
            ))}
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Days</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[180px]">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {employees.map((emp) => {
            const key = emp.documentId || String(emp.id);
            const row = rows[key] || { days: {}, description: '' };
            return (
              <tr key={key} className="hover:bg-gray-50">
                <td className="sticky left-0 bg-white px-4 py-2 font-medium text-gray-900 whitespace-nowrap z-10">
                  {emp.name}
                </td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{emp.rank || '-'}</td>
                {dayNumbers.map((day) => {
                  const status = row.days[String(day)] || 'off';
                  const display = statusDisplay[status];
                  return (
                    <td key={day} className="px-0.5 py-1 text-center">
                      <button
                        type="button"
                        title={`Day ${day}: ${display.title}`}
                        onClick={() => toggleDay(key, day)}
                        className={`w-7 h-7 rounded border text-xs font-bold ${display.cls} hover:opacity-75 transition-opacity`}
                      >
                        {display.symbol}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                  {countWorkedDays(row.days)}
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) => setDescription(key, e.target.value)}
                    placeholder="Notes..."
                    className={`${tableInputCls} min-w-[150px]`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {employees.length === 0 && (
        <p className="text-center text-gray-500 py-10">No employees are assigned to this vessel</p>
      )}
    </div>
  );
};

export default TimesheetGrid;
